import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import styled from "styled-components";

gsap.registerPlugin(ScrollTrigger);

// Custom Interactive Counter Component
const StatCounter = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const elementRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasStarted(true);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasStarted) return;

    const cleanNumber = parseInt(value.replace(/,/g, "").replace(/\+/g, "").replace(/K/g, ""));
    const isThousands = value.includes("K");
    let start = 0;
    const end = cleanNumber;
    if (start === end) return;

    let totalMilliseconds = duration;
    let incrementTime = 20;
    const totalSteps = totalMilliseconds / incrementTime;
    const step = Math.ceil(end / totalSteps);

    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        clearInterval(timer);
        setCount(end);
      } else {
        setCount(start);
      }
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, duration, hasStarted]);

  const formatNumber = (num) => {
    let formatted = num.toLocaleString();
    if (value.includes("K")) {
      formatted = num + "K";
    }
    if (value.includes("+")) {
      return formatted + "+";
    }
    return formatted;
  };

  return <span ref={elementRef}>{formatNumber(count)}</span>;
};

const HeroHomeTwo = () => {
  const cardRef = useRef(null);
  const statsRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const buttonsRef = useRef(null);
  const socialRef = useRef(null);

  useEffect(() => {
    let ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.from(cardRef.current, {
        opacity: 0,
        y: 60,
        duration: 1.2,
      })
        .from(
          titleRef.current,
          {
            opacity: 0,
            y: 35,
            duration: 0.8,
          },
          "-=0.7"
        )
        .from(
          subtitleRef.current,
          {
            opacity: 0,
            y: 20,
            duration: 0.8,
          },
          "-=0.6"
        )
        .from(
          buttonsRef.current,
          {
            opacity: 0,
            y: 20,
            duration: 0.8,
          },
          "-=0.6"
        )
        .from(
          statsRef.current,
          {
            opacity: 0,
            y: 40,
            duration: 1,
          },
          "-=0.6"
        )
        .from(
          socialRef.current,
          {
            opacity: 0,
            x: 40,
            duration: 0.8,
          },
          "-=0.8"
        );
    });

    return () => ctx.revert();
  }, []);

  return (
    <>
      <HeroWrapper>
        {/* Soft Background Aurora Glow Effects */}
        <GlowCircle size="380px" color="rgba(255, 107, 0, 0.045)" top="-10%" left="-15%" />
        <GlowCircle size="420px" color="rgba(255, 60, 0, 0.03)" top="30%" left="75%" />

        <HeroCard ref={cardRef}>
          {/* Card Window Control Dots (Decorative) */}
          <CardWindowControls>
            <span></span>
            <span></span>
            <span></span>
          </CardWindowControls>

          {/* Premium Badge */}
          <PremiumBadge>
            <span className="badge-dot"></span>
            Premium IT Solutions Partner
          </PremiumBadge>

          {/* Hero Typography */}
          <HeroTitle ref={titleRef}>
            India's Best Digital <br />
            <HighlightText>Marketing & IT Solutions</HighlightText> <br />
            Company
          </HeroTitle>

          <HeroSubtitle ref={subtitleRef}>
            Trusted by 100+ Businesses — SEO • Google Ads • Web Development • Software Solutions
          </HeroSubtitle>

          {/* Action Button Row */}
          <ButtonGroup ref={buttonsRef}>
            <PrimaryButton to="/contact_us">
              Get Free Consultation <span className="arrow">→</span>
            </PrimaryButton>
            <SecondaryButton to="/portfolio">
              View Our Work
            </SecondaryButton>
          </ButtonGroup>
        </HeroCard>

        {/* Overlapping White Stats Card */}
        <StatsCard ref={statsRef}>
          <StatsRow>
            {/* Column 1 */}
            <StatColumn>
              <IconWrapper>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </IconWrapper>
              <StatNumber>
                <StatCounter value="100+" />
              </StatNumber>
              <StatLabel>Clients Served</StatLabel>
            </StatColumn>

            {/* Column 2 */}
            <StatColumn>
              <IconWrapper>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="7" />
                  <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                </svg>
              </IconWrapper>
              <StatNumber>
                <StatCounter value="7+" />
              </StatNumber>
              <StatLabel>Years Experience</StatLabel>
            </StatColumn>

            {/* Column 3 */}
            <StatColumn>
              <IconWrapper>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
              </IconWrapper>
              <StatNumber>
                <StatCounter value="80K+" />
              </StatNumber>
              <StatLabel>Keywords Ranked</StatLabel>
            </StatColumn>

            {/* Column 4 */}
            <StatColumn>
              <IconWrapper>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
              </IconWrapper>
              <StatNumber>
                <StatCounter value="12+" />
              </StatNumber>
              <StatLabel>Services</StatLabel>
            </StatColumn>
          </StatsRow>
        </StatsCard>

        {/* Floating Social Media Sidebar */}
        <SocialSection ref={socialRef}>
          <SocialIcons>
            <SocialLink href="https://www.facebook.com/doaguruinfosystems/" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/>
              </svg>
            </SocialLink>
            <SocialLink href="https://twitter.com/DoaGuru" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </SocialLink>
            <SocialLink href="https://www.linkedin.com/company/doaguru-infosystem/" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
            </SocialLink>
            <SocialLink href="https://www.instagram.com/doaguru/" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
            </SocialLink>
          </SocialIcons>
          <SocialLine />
        </SocialSection>
      </HeroWrapper>

    </>
  );
};

export default HeroHomeTwo;

// ==================== Styled Components ====================

const HeroWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 100px 40px 10px 40px;
  background-color: #ffffff;
  position: relative;
  overflow: hidden;
  font-family: "Outfit", "Inter", sans-serif;

  @media (max-width: 1200px) {
    padding: 90px 24px 10px 24px;
  }
  @media (max-width: 768px) {
    padding: 80px 16px 10px 16px;
  }
`;

const GlowCircle = styled.div`
  position: absolute;
  width: ${(props) => props.size || "300px"};
  height: ${(props) => props.size || "300px"};
  background: ${(props) => props.color || "rgba(255, 106, 0, 0.05)"};
  filter: blur(100px);
  border-radius: 50%;
  top: ${(props) => props.top || "0"};
  left: ${(props) => props.left || "0"};
  pointer-events: none;
  z-index: 1;
`;

const HeroCard = styled.div`
  background: linear-gradient(135deg, #090a10 0%, #121420 100%);
  border-radius: 32px;
  max-width: 1200px;
  width: 100%;
  padding: 70px 40px 120px 40px;
  text-align: center;
  position: relative;
  z-index: 2;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    padding: 50px 20px 90px 20px;
    border-radius: 24px;
  }
`;

const CardWindowControls = styled.div`
  position: absolute;
  top: 32px;
  right: 40px;
  display: flex;
  gap: 8px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #ff6a00;
    opacity: 0.75;
  }

  @media (max-width: 768px) {
    top: 24px;
    right: 24px;
  }
`;

const PremiumBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 106, 0, 0.08);
  border: 1px solid rgba(255, 106, 0, 0.15);
  border-radius: 100px;
  padding: 6px 16px;
  color: #ff6a00;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 35px;
  text-transform: uppercase;

  .badge-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #ff6a00;
    box-shadow: 0 0 10px #ff6a00;
  }

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 4px 12px;
    margin-bottom: 25px;
  }
`;

const HeroTitle = styled.h1`
  color: #ffffff;
  font-size: 54px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -1px;
  margin-bottom: 25px;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 991px) {
    font-size: 42px;
  }
  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 1.2;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(135deg, #ff7e00 0%, #ff3a00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
`;

const HeroSubtitle = styled.p`
  color: #94a3b8;
  font-size: 18px;
  line-height: 1.6;
  font-weight: 400;
  max-width: 680px;
  margin: 0 auto 40px auto;

  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 30px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  z-index: 5;
`;

const PrimaryButton = styled(Link)`
  background: linear-gradient(135deg, #ff7e00 0%, #ff4500 100%);
  color: #ffffff;
  border: none;
  font-family: "Outfit", sans-serif;
  font-size: 16px;
  font-weight: 600;
  padding: 15px 35px;
  border-radius: 100px;
  cursor: pointer;
  box-shadow: 0 10px 25px rgba(255, 78, 0, 0.3);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;

  .arrow {
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(24, 23, 23, 0.4);

    .arrow {
      transform: translateX(4px);
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 576px) {
    width: 100%;
    justify-content: center;
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.15);
  font-family: "Outfit", sans-serif;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 34px;
  border-radius: 100px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  text-decoration: none;
  display: inline-block;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 576px) {
    width: 100%;
    text-align: center;
  }
`;

// ==================== Overlapping White Stats Card ====================

const StatsCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.05);
  max-width: 1060px;
  width: 90%;
  margin-top: -65px;
  z-index: 10;
  padding: 1cs_height_1000px 20px;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.03);

  @media (max-width: 991px) {
    width: 95%;
  }

  @media (max-width: 768px) {
    margin-top: -50px;
    padding: 24px 16px;
    border-radius: 20px;
  }
`;

const StatsRow = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
`;

const StatColumn = styled.div`
  flex: 1;
  min-width: 200px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 15px;
  position: relative;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: 0;
    top: 15%;
    height: 70%;
    width: 1px;
    background-color: #f1f5f9;
  }

  @media (max-width: 991px) {
    min-width: 50%;
    margin-bottom: 25px;

    &:nth-child(2n)::after {
      display: none;
    }
  }

  @media (max-width: 576px) {
    min-width: 100%;
    margin-bottom: 25px;

    &::after {
      display: none !important;
    }

    &:not(:last-child) {
      border-bottom: 1px solid #f1f5f9;
      padding-bottom: 25px;
    }
  }
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 106, 0, 0.06);
  color: #ff6a00;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  transition: all 0.3s ease;

  ${StatColumn}:hover & {
    background: #ff6a00;
    color: #ffffff;
    transform: translateY(-2px);
  }
`;

const StatNumber = styled.div`
  color: #0f172a;
  font-size: 34px;
  font-weight: 800;
  margin-bottom: 6px;
  line-height: 1;
  font-family: "Outfit", sans-serif;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const StatLabel = styled.div`
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
`;

// ==================== Social Media Sidebar ====================

const SocialSection = styled.div`
  position: absolute;
  right: 45px;
  top: 40%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  z-index: 10;

  @media (max-width: 1300px) {
    right: 20px;
  }

  @media (max-width: 1100px) {
    position: static;
    transform: none;
    flex-direction: column-reverse;
    justify-content: center;
    width: 100%;
    margin-top: 35px;
    margin-bottom: 10px;
  }
`;

const SocialLine = styled.div`
  width: 1px;
  height: 70px;
  background: linear-gradient(to bottom, #cbd5e1 0%, rgba(203, 213, 225, 0) 100%);

  @media (max-width: 1100px) {
    display: none;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (max-width: 1100px) {
    flex-direction: row;
    gap: 20px;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #ffffff;
  color: #64748b;
  border: 1px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02);

  svg {
    transition: transform 0.3s ease;
  }

  &:hover {
    background: #ff6a00;
    color: #ffffff;
    border-color: #ff6a00;
    transform: translateY(-3px) scale(1.08);
    box-shadow: 0 6px 15px rgba(255, 106, 0, 0.25);

    svg {
      transform: rotate(8deg);
    }
  }
`;
